import axios from "axios";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { configureRetry, DEFAULT_TIMEOUT } from "../utils/axiosRetryConfig";
import { cookieOptions } from "../utils/cookieConfig";
import { emitFrontendTelemetry } from "../utils/frontendTelemetry.js";
import {
    isTemporaryApiFailure,
    notifyApiDegraded,
    notifyApiHealthy,
} from "../utils/apiAvailability.js";
import { normalizeSentryPath } from "../utils/sentryUtils.js";

// Normalize API URL by removing trailing slashes
const normalizeApiUrl = (url) => {
    return url ? url.replace(/\/+$/, '') : url;
};

// Create an axios instance for client portal API calls
const clientAPI = axios.create({
    baseURL: normalizeApiUrl(process.env.VITE_API_URL || process.env.VITE_NODE_API_URL || 'http://localhost:3000'),
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT,
});

// Retry must be configured BEFORE the response interceptor so that
// intermediate retry attempts never trigger login redirects.
configureRetry(clientAPI);

const cookies = new Cookies();

// Add auth token from tokenRC cookie
clientAPI.interceptors.request.use(function (config) {
    // Normalize double slashes in URL path (but not in protocol "://")
    if (config.url) {
        config.url = config.url.replace(/([^:]\/)\/+/g, '$1');
    }

    let token = cookies.get("tokenRC");

    if (token) {
        // Add Bearer prefix if not already present
        if (!token.startsWith('Bearer ')) {
            token = `Bearer ${token}`;
        }
        config.headers.Authorization = token;
    }

    return config;
}, function (error) {
    return Promise.reject(error);
});

// Handle 401 -> redirect to client login
clientAPI.interceptors.response.use(
    (response) => {
        notifyApiHealthy({ source: "api_response" });
        return response;
    },
    (error) => {
        const method = error.config?.method?.toUpperCase();
        const url = error.config?.url;
        const requestId = error.response?.headers?.["x-request-id"] || error.response?.headers?.["X-Request-Id"];
        const retryCount = Number.isInteger(error.config?.["axios-retry"]?.retryCount)
            ? error.config["axios-retry"].retryCount
            : null;

        if (isTemporaryApiFailure(error)) {
            notifyApiDegraded("api_request_failed", {
                source: "api_response",
                statusCode: error.response?.status || null,
                method,
                url,
                code: error.code || null,
            });
        }

        if (error.response) {
            const status = error.response.status;

            if (status === 401) {
                notifyApiHealthy({ source: "client_auth_response", statusCode: 401 });

                // Clear client cookies
                cookies.remove("tokenRC", cookieOptions);
                cookies.remove("userRC", cookieOptions);
                cookies.remove("userRCId", cookieOptions);

                // Don't redirect if already on the client login page
                if (window.location.pathname !== '/client-section/login') {
                    window.location.href = '/client-section/login';
                }
            }

            // Sentry: breadcrumb for 4xx (except 401), captureException for 5xx (except 503)
            if (status >= 400 && status < 500 && status !== 401) {
                Sentry.addBreadcrumb({
                    category: "api.error",
                    message: `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status}`,
                    level: "warning",
                    data: { api: "client-portal", status },
                });
            } else if (status >= 500 && status !== 503) {
                void emitFrontendTelemetry("frontend.api.failure", {
                    area: "api",
                    action: "request_failed",
                    method,
                    url,
                    statusCode: status,
                    requestId,
                    retryCount,
                    errorCode: error.code,
                    message: error.message,
                });
                const normalizedPath = normalizeSentryPath(error.config?.url);
                // Backend 5xx errors are captured by backend Sentry with full server context.
                // Record as a breadcrumb rather than a duplicate top-level event.
                Sentry.addBreadcrumb({
                    category: "api.error",
                    message: `${method} ${url} → ${status}`,
                    level: "error",
                    data: { api: "client-portal", status, requestId, url_path: normalizedPath },
                });
            }
        } else if (error.code !== 'ERR_CANCELED') {
            void emitFrontendTelemetry("frontend.api.failure", {
                area: "api",
                action: "network_failure",
                method,
                url,
                retryCount,
                errorCode: error.code,
                message: error.message,
            });
        }
        return Promise.reject(error);
    }
);

export default clientAPI;
